import React, { ReactNode, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { CustomLabel } from "./CustomLabel";
import { InputErrorMessage } from "./InputErrorMessage";
import { Controller, Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { FaRegEye, FaEyeSlash, FaClipboard } from "react-icons/fa";

type TextInputWithoutValidationProps = {
  usarValidacaoParaFormulario: false;
  isPassword?: boolean;
};

type TextInputWithValidationProps = {
  usarValidacaoParaFormulario: true;
  /**Nome do atributo que será usado no formulário para realizar as validações*/
  name: string;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  isPassword?: boolean;
};

/**
 * Novas props adicionadas:
 *  - readOnly?: boolean
 *  - enableCopy?: boolean
 */
type TextInputProps = {
  id: string;
  label?: string;
  placeholder?: string;
  className?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  defaultValue?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  required?: boolean;
  autoFocus?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  maxLength?: number;

  /**
   * Se true, torna o campo somente leitura (sem permitir digitação).
   * Mantém a aparência de campo habilitado.
   */
  readOnly?: boolean;

  /**
   * Se true, exibe um botão (ícone) para copiar o valor do campo
   * para a área de transferência (clipboard).
   */
  enableCopy?: boolean;
} & (TextInputWithoutValidationProps | TextInputWithValidationProps);

const TextInput: React.FC<TextInputProps> = (props) => {
  const [showPassword, setShowPassword] = React.useState(false);

  // Estado para exibir mensagem de "Copiado!"
  const [copiado, setCopiado] = useState(false);

  // Função para copiar o valor para a área de transferência
  const handleCopy = (value: string) => {
    if (!value) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(() => {
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
      });
    } else {
      // Fallback: Usa um input escondido caso o clipboard API não esteja disponível
      const input = document.createElement("input");
      input.value = value;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }
  };

  const getType = () => {
    let type = "text";
    if (props.isPassword) {
      type = showPassword ? "text" : "password";
    }
    return type;
  };

  // -----------------------------------------------------------------------------------------
  // MODO 1: Com Validação (React Hook Form)
  // -----------------------------------------------------------------------------------------
  const renderTextInputComValidacao = (): ReactNode => {
    if (props.usarValidacaoParaFormulario) {
      return (
        <Controller
          name={props.name}
          control={props.control}
          defaultValue={props.defaultValue}
          render={({ field, fieldState }) => (
            <>
              {/* Label (se houver) */}
              {props.label && (
                <CustomLabel possuiErro={fieldState.error != null}>
                  {props.required ? `${props.label} *` : props.label}
                </CustomLabel>
              )}

              <div className="d-flex">
                {/* Input principal */}
                <Form.Control
                  {...props.register(props.name)}
                  {...field}
                  id={props.id}
                  className={`input-gc ${props.isPassword ? "no-border-radius-right" : ""}`}
                  placeholder={props.placeholder || ""}
                  type={getType()}
                  readOnly={props.readOnly}
                  maxLength={props.maxLength}
                  isInvalid={fieldState.error != null}
                  value={field.value || ""}
                  {...(props.autoComplete !== undefined ? { autoComplete: props.autoComplete } : {})}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    // Atualiza o valor no React Hook Form
                    field.onChange(event);
                    props.setValue(props.name, event.target.value);
                    // Dispara o onChange externo, se houver
                    props.onChange && props.onChange(event);
                  }}
                  onKeyDown={props.onKeyDown}
                  autoFocus={props.autoFocus}
                  onBlur={props.onBlur}
                  required={props.required}
                />

                {/* Ícone de mostrar/ocultar senha, se for isPassword */}
                {props.isPassword && (
                  <InputGroup.Text
                    className={fieldState.error != null ? "input-gc-icon-error" : "input-gc-icon"}
                    onMouseDown={() => setShowPassword(true)}
                    onMouseUp={() => setShowPassword(false)}
                    aria-describedby="basic-eye"
                    style={{ cursor: "pointer" }}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaRegEye />}
                  </InputGroup.Text>
                )}

                {/* Ícone de copiar, se enableCopy for true */}
                {props.enableCopy && (
                  <InputGroup.Text
                    className={fieldState.error != null ? "input-gc-icon-error" : "input-gc-icon"}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleCopy(field.value || "")}
                  >
                    <FaClipboard />
                  </InputGroup.Text>
                )}
              </div>

              {/* Mensagem de erro do React Hook Form */}
              {fieldState.error && <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>}

              {/* Mensagem de "Copiado!" (se acabou de copiar) */}
              {copiado && (
                <div style={{ color: "green", fontSize: "0.85rem", marginTop: 4 }}>
                  Copiado!
                </div>
              )}
            </>
          )}
        />
      );
    }
  };

  // -----------------------------------------------------------------------------------------
  // MODO 2: Sem Validação
  // -----------------------------------------------------------------------------------------
  const renderTextInputSemValidacao = (): ReactNode => {
    if (!props.usarValidacaoParaFormulario) {
      return (
        <>
          {/* Label (se houver) */}
          {props.label && <CustomLabel possuiErro={false}>{props.label}</CustomLabel>}

          <div className="d-flex">
            {/* Input principal */}
            <Form.Control
              id={props.id}
              className="input-gc"
              required={props.required}
              placeholder={props.placeholder || ""}
              type={getType()}
              readOnly={props.readOnly}
              defaultValue={props.defaultValue}
              {...(props.autoComplete !== undefined ? { autoComplete: props.autoComplete } : {})}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => props.onChange && props.onChange(event)}
              autoFocus={props.autoFocus}
              onBlur={props.onBlur}
              onKeyDown={props.onKeyDown}
            />

            {/* Ícone de mostrar/ocultar senha, se for isPassword */}
            {props.isPassword && (
              <InputGroup.Text
                className="input-gc-icon"
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                aria-describedby="basic-eye"
                style={{ cursor: "pointer" }}
              >
                {showPassword ? <FaEyeSlash /> : <FaRegEye />}
              </InputGroup.Text>
            )}

            {/* Ícone de copiar, se enableCopy for true */}
            {props.enableCopy && (
              <InputGroup.Text
                className="input-gc-icon"
                style={{ cursor: "pointer" }}
                onClick={() => handleCopy(props.defaultValue || "")}
              >
                <FaClipboard />
              </InputGroup.Text>
            )}
          </div>

          {/* Mensagem de "Copiado!" (se acabou de copiar) */}
          {copiado && (
            <div style={{ color: "green", fontSize: "0.85rem", marginTop: 4 }}>
              Copiado!
            </div>
          )}
        </>
      );
    }
  };

  return (
    <div className={props.className || ""}>
      {/* Se usar validação, renderiza o modo com validação */}
      {props.usarValidacaoParaFormulario && renderTextInputComValidacao()}

      {/* Se não usar validação, renderiza o modo sem validação */}
      {!props.usarValidacaoParaFormulario && renderTextInputSemValidacao()}
    </div>
  );
};

export default TextInput;